import { Batten } from '../pieces/Batten';
import { Corner } from '../pieces/Corner';
import { CrossPiece } from '../pieces/CrossPiece';
import { Pane } from '../pieces/Pane';
import { Door } from '../pieces/Door';
import { Plate } from '../pieces/Plate';
import {
  BattensEntity,
  CornersEntity,
  CrossPieceEntity,
  DoorHandleEntity,
  DoorsEntity,
  DrawersEntity,
  DrawersHandleEntity,
  IntermediateShelfEntity,
  PanesEntity,
  PieceEntity,
  Pieces,
  PiecesFromJson,
  PiecesJson,
  PlatesEntity,
  ReinforcementIntermediateShelfEntity,
  RodEntity,
  SupportIntermediateShelfEntity,
  UpperShelfEntity,
} from '../Data';
import { DataStorage, setPiecesData } from '../utils/dataStorage';
import { PiecesComponentsList } from './PiecesComponentsList';
import { Drawer } from '../pieces/Drawer';
import { IntermediateShelf } from '../pieces/IntermediateShelf';
import { SupportIntermediateShelf } from '../pieces/SupportIntermediateShelf';
import { DrawerSlat } from '../pieces/DrawerSlat';
import { Rod } from '../pieces/Rod';
import { DoorHandle } from '../pieces/DoorHandle';
import { DrawerHandle } from '../pieces/DrawerHandle';
import { SimpleComponent } from '../pieces/SimpleComponent';
import { ReinforcementIntermediateShelf } from '../pieces/ReinforcementIntermediateShelf';
import { UpperShelf } from '../pieces/UpperShelf';
import { WheelsBase } from '../pieces/WheelsBase';

export class PiecesList {
  battens: PiecesComponentsList<Batten>;
  corners: PiecesComponentsList<Corner>;
  crossPieces: PiecesComponentsList<CrossPiece>;
  panes: PiecesComponentsList<Pane>;
  wheelsBases: PiecesComponentsList<WheelsBase>;
  doors: PiecesComponentsList<Door>;
  doorsHandles: PiecesComponentsList<DoorHandle>;
  plates: PiecesComponentsList<Plate>;
  drawers: PiecesComponentsList<Drawer>;
  drawersHandles: PiecesComponentsList<DrawerHandle>;
  drawersSlat: PiecesComponentsList<DrawerSlat>;
  intermediatesShelves: PiecesComponentsList<IntermediateShelf>;
  supportIntermediatesShelves: PiecesComponentsList<SupportIntermediateShelf>;
  reinforcementIntermediatesShelves: PiecesComponentsList<
    ReinforcementIntermediateShelf
  >;
  upperShelves: PiecesComponentsList<UpperShelf>;
  rods: PiecesComponentsList<Rod>;
  others: PiecesComponentsList<SimpleComponent>;

  constructor(piecesJSON: string, save = true) {
    if (!DataStorage.colors || DataStorage.colors.length === 0) {
      throw new Error(
        'Colors must be loaded before we can create the pieces list',
      );
    }

    const rawPieces: PiecesFromJson = JSON.parse(piecesJSON, this.revivePieces);

    this.battens = new PiecesComponentsList<Batten>(rawPieces.battens);
    this.corners = new PiecesComponentsList<Corner>(rawPieces.corners);
    this.crossPieces = new PiecesComponentsList<CrossPiece>(
      rawPieces.crossPieces,
    );
    this.panes = new PiecesComponentsList<Pane>(
      rawPieces.panes.filter(e => e.drilling === null),
    );
    this.wheelsBases = new PiecesComponentsList<WheelsBase>(
      rawPieces.panes.filter(e => e.drilling !== null),
    );
    this.doors = new PiecesComponentsList<Door>(rawPieces.doors);
    this.doorsHandles = new PiecesComponentsList<DoorHandle>(
      rawPieces.doorsHandles,
    );
    this.plates = new PiecesComponentsList<Plate>(rawPieces.plates);
    this.drawers = new PiecesComponentsList<Drawer>(rawPieces.drawers);
    this.drawersHandles = new PiecesComponentsList<DrawerHandle>(
      rawPieces.drawersHandles,
    );
    this.drawersSlat = new PiecesComponentsList<DrawerSlat>(
      rawPieces.drawersSlat,
    );
    this.intermediatesShelves = new PiecesComponentsList<IntermediateShelf>(
      rawPieces.intermediatesShelves,
    );
    this.supportIntermediatesShelves = new PiecesComponentsList<
      SupportIntermediateShelf
    >(rawPieces.supportIntermediatesShelves);
    this.reinforcementIntermediatesShelves = new PiecesComponentsList<
      ReinforcementIntermediateShelf
    >(rawPieces.reinforcementIntermediatesShelves);
    this.upperShelves = new PiecesComponentsList<UpperShelf>(
      rawPieces.upperShelves,
    );
    this.rods = new PiecesComponentsList<Rod>(rawPieces.rods);
    this.others = new PiecesComponentsList<SimpleComponent>(rawPieces.others);

    //Store the pieces data for global access
    if (save) {
      setPiecesData(this);
      // Save to localStorage
      // localStorage.setItem('piecesData', JSON.stringify(this));
    }
  }

  toJSON(): PiecesJson {
    return Object.assign({}, this, {
      battens: Array.from(this.battens.list.values()),
      corners: Array.from(this.corners.list.values()),
      crossPieces: Array.from(this.crossPieces.list.values()),
      panes: Array.from(this.panes.list.values()).concat(
        Array.from(this.wheelsBases.list.values()),
      ),
      doors: Array.from(this.doors.list.values()),
      doorsHandles: Array.from(this.doorsHandles.list.values()),
      plates: Array.from(this.plates.list.values()),
      drawers: Array.from(this.drawers.list.values()),
      drawersHandles: Array.from(this.drawersHandles.list.values()),
      intermediateShelves: Array.from(this.intermediatesShelves.list.values()),

      supportIntermediatesShelves: Array.from(
        this.supportIntermediatesShelves.list.values(),
      ),
      reinforcementIntermediatesShelves: Array.from(
        this.supportIntermediatesShelves.list.values(),
      ),
      upperShelves: Array.from(this.upperShelves.list.values()),
    });
  }

  revivePieces(key: string, value: any) {
    switch (key) {
      case 'battens':
        return (value as BattensEntity[]).map(e => Batten.fromJSON(e));
      case 'corners':
        return (value as CornersEntity[]).map(e => Corner.fromJSON(e));
      case 'crossPieces':
        return (value as CrossPieceEntity[]).map(e => CrossPiece.fromJSON(e));
      case 'panes':
        return (value as PanesEntity[]).map(e =>
          e.drilling ? WheelsBase.fromJSON(e) : Pane.fromJSON(e),
        );
      case 'doors':
        return (value as DoorsEntity[]).map(e => Door.fromJSON(e));
      case 'doorsHandles':
        return (value as DoorHandleEntity[]).map(e => DoorHandle.fromJSON(e));
      case 'plates':
        return (value as PlatesEntity[]).map(e => Plate.fromJSON(e));
      case 'drawers':
        return (value as DrawersEntity[]).map(e => Drawer.fromJSON(e));
      case 'drawersHandles':
        return (value as DrawersHandleEntity[]).map(e =>
          DrawerHandle.fromJSON(e),
        );
      case 'drawersSlat':
        return (value as RodEntity[]).map(e => DrawerSlat.fromJSON(e));
      case 'intermediatesShelves':
        return (value as IntermediateShelfEntity[]).map(e =>
          IntermediateShelf.fromJSON(e),
        );
      case 'supportIntermediatesShelves':
        return (value as SupportIntermediateShelfEntity[]).map(e =>
          SupportIntermediateShelf.fromJSON(e),
        );
      case 'reinforcementIntermediatesShelves':
        return (value as ReinforcementIntermediateShelfEntity[]).map(e =>
          ReinforcementIntermediateShelf.fromJSON(e),
        );
      case 'upperShelves':
        return (value as UpperShelfEntity[]).map(e => UpperShelf.fromJSON(e));
      case 'rods':
        return (value as RodEntity[]).map(e => Rod.fromJSON(e));
      case 'others':
        return (value as PieceEntity[]).map(e => SimpleComponent.fromJSON(e));
      case 'feetsExtensions':
        return (value as PieceEntity[]).map(e => SimpleComponent.fromJSON(e));
      default:
        return value;
    }
  }
}
